import { useEffect, useMemo, useState } from "react";

/**
 * Returns analytics card data for fitting room sessions and value tried on within the given date range.
 * - value: number of shoppers (sessions) in compact form (e.g., 1.2K)
 * - subtitle: currency-adjusted compact total tried on value (e.g., £1.2K Tried On)
 */
import type { DeviceType } from "@/components/filters/DeviceTypeFilter";
import i18n from "@/i18n/i18n";
import { AnalyticsServiceV2 } from "@/services/features/AnalyticsServiceV2";
import { Store } from "@/types/store";
import {
  CurrencyCode,
  formatCurrencyWithNoDecimals,
  getStoreCurrencyIdentifiers,
} from "@/utils/currency";
import { getBrowserTimezone } from "@/utils/storeTime";
import { useLatestRequestGuard } from "@/hooks/useLatestRequestGuard";

import {
  AnalyticsCardData,
  DateRange,
  storePerformanceService,
} from "../services/StorePerformanceService";

export const useTriedOnValue = (
  stores: Store[] | null,
  dateRange?: DateRange,
  deviceType?: DeviceType,
  areaIds?: number[],
): AnalyticsCardData => {
  const areaIdsKey = areaIds?.join(",") ?? "";
  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  const browserTimezone = getBrowserTimezone();
  const [rawData, setRawData] = useState<{
    totalSessions: number;
    totalSessionValue: number;
  } | null>(null);
  const beginRequest = useLatestRequestGuard();

  // helpers
  const formatCompactNumber = (num: number): string => {
    try {
      return new Intl.NumberFormat(currentLanguage || undefined, {
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(num);
    } catch {
      // fallback
      if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
      if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
      return String(num);
    }
  };

  const [currencyCode, currencySymbol] = getStoreCurrencyIdentifiers(stores);

  useEffect(() => {
    const isCurrent = beginRequest();
    const ids = stores?.map((store) => store.id) ?? [];
    if (ids.length === 0 || !dateRange) {
      setRawData(null);
      return;
    }

    const fetchTriedOnValue = async () => {
      try {
        const analyticsService = AnalyticsServiceV2.getInstance();
        console.log(
          `Fetching tried on value for from ${storePerformanceService.formatDate(dateRange.start, browserTimezone)} to ${storePerformanceService.formatDate(dateRange.end, browserTimezone)}`,
        );
        const params: any = {
          storeIds: ids,
          startDate: storePerformanceService.formatDate(dateRange.start, browserTimezone),
          endDate: storePerformanceService.formatDate(dateRange.end, browserTimezone),
        };
        if (deviceType && deviceType !== "all") params.deviceType = deviceType;
        if (areaIdsKey) params.areaIds = areaIdsKey.split(",").map(Number);
        const response = await analyticsService.getSessionAnalytics(params);

        const totalSessions =
          deviceType === "IN_AISLE"
            ? (response?.data?.totalSessionsIncludingNoItems ?? 0)
            : (response?.data?.totalSessions ?? 0);
        const totalSessionValue = response?.data?.totalSessionValue ?? 0;

        if (!totalSessions && !totalSessionValue) {
          if (isCurrent()) setRawData(null);
          return;
        }
        if (isCurrent())
          setRawData({ totalSessions, totalSessionValue: Number(totalSessionValue) });
      } catch (e) {
        console.error("Error fetching tried on value:", e);
        if (isCurrent()) setRawData(null);
      }
    };

    fetchTriedOnValue();
  }, [
    stores ? stores.map((store) => store.id).join(",") : null,
    browserTimezone,
    storePerformanceService.formatDate(dateRange?.start, browserTimezone),
    storePerformanceService.formatDate(dateRange?.end, browserTimezone),
    deviceType,
    areaIdsKey,
  ]);

  return useMemo(() => {
    if (!rawData) {
      return {
        previousValue: null,
        isPositive: true,
        pillText: "",
        subtitle: i18n.t("common.analytics.noDataForTimeframe"),
        value: "--",
        rawValue: null,
      };
    }

    const mainValue = rawData.totalSessions ? formatCompactNumber(rawData.totalSessions) : "--";
    const formattedValue = formatCurrencyWithNoDecimals(
      Math.round(rawData.totalSessionValue),
      currencyCode as CurrencyCode,
      currencySymbol,
    );

    return {
      previousValue: null,
      value: mainValue,
      subtitle: rawData.totalSessionValue
        ? i18n.t("common.analytics.triedOnValue", { value: formattedValue })
        : "",
      isPositive: true,
      pillText: "",
      rawValue: rawData.totalSessions,
    };
  }, [rawData, currentLanguage, currencyCode, currencySymbol]);
};
